import React, { useState, useEffect } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  ConnectionVariableModel,
  ConnectionVariableKind,
} from '@mm/state/model/ConnectionVariableModel';
import { StoreState } from '@mm/state/store-interfaces';
import VariablesCRUDActions from '@mm/state/actions/VariablesCRUDActions';
import VariableItem from './VariableItem/VariableItem';
import {
  blankVariable,
  httpHeaderVariable,
  createConnectionTag,
} from '@mm/state/helpers/connection-helpers';
import './EditConnectionAttributes.pcss';
import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import { ConnectionTagSelect } from './ConnectionTagSelect';
import { ConnectionTagModel } from '@mm/state/model/ConnectionTagModel';
import { DropdownMenu } from '@mm/components';
import tagIcon from 'assets/attributes/tag.svg';
import attributeIcon from 'assets/attributes/attribute.svg';
import httpHeaderIcon from 'assets/attributes/http-header.svg';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import {
  ControlOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';

type Props = {
  tags: ConnectionTagModel[];
  facebookPageId: number;
  variablesToSend: ConnectionVariableModel[];
  onVariablesChange: (variables: ConnectionVariableModel[]) => void;
  onTagsChange: (tags: ConnectionTagModel[]) => void;
  filterMenuItems?: (menuItem: string) => boolean;
};

const AttributeType = ({ icon, name }: { icon: string; name: string }) => (
  <div className="attribute-type">
    <img className="attribute-type__icon" src={icon} alt={name} />
    <span className="attribute-type__name">{name}</span>
  </div>
);

export const EditConnectionAttributes = ({
  tags,
  variablesToSend,
  onVariablesChange,
  onTagsChange,
  facebookPageId,
  filterMenuItems = () => true,
}: Props) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const initialVariables =
    variablesToSend.length > 0 ? variablesToSend : [blankVariable()];
  const [localVariables, setLocalVariables] = useState(initialVariables);

  useEffect(() => {
    dispatch(VariablesCRUDActions.retrieveAll(facebookPageId));
  }, []);

  useEffect(() => {
    dispatch(TagsCRUDActions.getAll(facebookPageId));
  }, [facebookPageId]);

  const [connectionTags, setConnectionTags] = useState(tags);
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );

  const filteredTagOptions = availableTags.filter(
    ({ name }) =>
      !connectionTags.find(
        ({ name: connectionTagName }) => connectionTagName === name,
      ),
  );

  const onAddVariable = (variable: ConnectionVariableModel) =>
    setLocalVariables([...localVariables, variable]);

  const onRemoveVariable = (index: number) => {
    const nextVariables = [
      ...localVariables.slice(0, index),
      ...localVariables.slice(index + 1),
    ];

    setLocalVariables(nextVariables);
    onVariablesChange(nextVariables);
  };

  const onVariableUpdate = (
    variable: ConnectionVariableModel,
    index: number,
  ) => {
    const nextVariables = [
      ...localVariables.slice(0, index),
      variable,
      ...localVariables.slice(index + 1),
    ];

    setLocalVariables(nextVariables);
    onVariablesChange(nextVariables);
  };

  const onAddTagBtnClick = () =>
    setConnectionTags([...connectionTags, createConnectionTag()]);

  const filterBlankTags = (tags: ConnectionTagModel[]) =>
    tags.filter(({ name }) => name);

  const onTagUpdate = (tag: ConnectionTagModel, i: number) => {
    const up = [
      ...connectionTags.slice(0, i),
      tag,
      ...connectionTags.slice(i + 1),
    ];
    setConnectionTags(up);
    onTagsChange(filterBlankTags(up));
  };

  const onRemoveTag = (i: number) => {
    const up = [...connectionTags.slice(0, i), ...connectionTags.slice(i + 1)];
    setConnectionTags(up);
    onTagsChange(filterBlankTags(up));
  };

  const menuItemClassName = 'edit-connection-attributes__menu-item';

  const menuItems = {
    attribute: {
      name: 'Attribute',
      icon: attributeIcon,
      className: menuItemClassName,
      onClick: () => onAddVariable(blankVariable()),
    },
    static: {
      name: 'Static Parameter',
      icon: <ControlOutlined className="attribute--contorl-icon" />,
      className: menuItemClassName,
      onClick: () =>
        onAddVariable(blankVariable(ConnectionVariableKind.toSend, true)),
    },
    httpHeader: {
      name: 'HTTP Header',
      icon: httpHeaderIcon,
      className: menuItemClassName,
      onClick: () => onAddVariable(httpHeaderVariable),
    },
    tag: {
      name: 'Tag',
      icon: tagIcon,
      className: menuItemClassName,
      onClick: onAddTagBtnClick,
    },
  };

  const getVariableType = (variable: ConnectionVariableModel) => {
    let type = menuItems.attribute;
    if (variable.static) type = menuItems.static;
    if (variable.header) type = menuItems.httpHeader;
    return { name: type.name, icon: type.icon };
  };

  return (
    <div>
      <div className="edit-connection-attributes__title-row">
        <div className="edit-connection-panel__title">Type</div>
        <div className="edit-connection-panel__title">Name</div>
        <div className="edit-connection-panel__title">Value</div>
      </div>
      {localVariables.map((variable, index) => (
        <div className="edit-connection-attributes__attribute" key={index}>
          <AttributeType {...getVariableType(variable)} />
          <VariableItem
            index={index}
            key={variable.id}
            variable={variable}
            onRemove={() => onRemoveVariable(index)}
            onVariableUpdate={onVariableUpdate}
            connectionVariables={localVariables}
          />
        </div>
      ))}
      {connectionTags.map((tag, i) => (
        <div className="edit-connection-attributes__attribute" key={i}>
          <AttributeType icon={menuItems.tag.icon} name={menuItems.tag.name} />
          <div className="edit-connection-panel__tags-select-container">
            <ConnectionTagSelect
              className="edit-connection-panel__tags-select"
              index={i}
              key={tag.sentAs}
              tag={tag}
              options={filteredTagOptions}
              onChange={onTagUpdate}
            />
          </div>

          <DeleteOutlined
            className="edit-connection-panel__tags-remove"
            onClick={() => onRemoveTag(i)}
          />
        </div>
      ))}

      <DropdownMenu
        className="edit-connection-attributes__menu"
        menuItems={Object.values(menuItems).filter(v =>
          filterMenuItems(v.name),
        )}
        menuButton={open => (
          <span className="edit-connection-panel__action" onClick={open}>
            <PlusOutlined
              className="edit-connection-panel__plus-icon"
              color="#2f54eb"
            />
            New
          </span>
        )}
      />
    </div>
  );
};

export default EditConnectionAttributes;
